/**
 * Unification of Google Maps and Mapbox API fusionLib.
 *
 * THIS SOFTWARE IS PROVIDED "AS IS" AND WITHOUT ANY EXPRESS OR IMPLIED
 * WARRANTIES, INCLUDING, WITHOUT LIMITATION, THE IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE.
 *
 * @copyright Copyright (c) 2018 - 2023 fusionLib. All rights reserved.
 * @link http://fusionlib.com
 */

export var flMaps = {
	/**
	 * Tracking for the API loading states.
	 */
	apiState: {
		google: {
			key: '',
			loading: false,
			loaded: false,
			readyFunctions: []
		},
		mapbox: {
			key: '',
			loading: false,
			loaded: false,
			readyFunctions: [],
			geocoderLoading: false,
			geocoderLoaded: false,
			geocoderReady: []
		}
	},

	/**
	 * Count of the number of maps on the page.
	 */
	mapCount: 1,

	/**
	 * The defaults.
	 */
	mapDefaults: {
		driver: 'google',
		style: '',
		satellite: ''
	},

	/**
	 * Set the API keys for the map libraries.
	 *
	 * @param {map} keys The list of API keys
	 * @return {object} This object.
	 */
	setAPIKeys: function(keys) {
		// keys is a map of google: key, mapbox: key
		for(var p in flMaps.apiState) {
			if(typeof keys[p] !== 'undefined')
				flMaps.apiState[p].key = keys[p];
		}

		return this;
	},

	/**
	 * Set the defaults the API will use.
	 *
	 * @param {map} The defaults.
	 * @return {object} This object.
	 */
	setDefaults: function(defaults) {
		mapDefaults = defaults;
		return this;
	},

	/**
	 * The map object.
	 *
	 * @param {map} mapOpts The options for the map.
	 * @param {function} callback function The callback function to invoke once initialised.
	 */
	Map: function(mapOpts, callback) {
		this._map = null;
		this.driver = '';
		this.panStart = { x: 0, y: 0 };
		this.cornerSize = {
			topLeft: { w: 0, h: 0},
			topRight: { w: 0, h: 0},
			bottomLeft: { w: 0, h: 0},
			bottomRight: { w: 0, h: 0}
		};

		mapOpts.driver = mapOpts.driver || mapDefaults.driver;
		mapOpts.style = mapOpts.style || mapDefaults.style;
		mapOpts.satellite = mapOpts.satellite || mapDefaults.satellite;
		mapOpts.zoom = mapOpts.zoom || 10;
		mapOpts.controls = mapOpts.controls || {};
		mapOpts.controls.zoom = mapOpts.controls.zoom || {}
		mapOpts.controls.zoom.show = mapOpts.controls.zoom.show || false;
		mapOpts.controls.zoom.position = mapOpts.controls.zoom.position || 'bottom-right';
		mapOpts.controls.geocoder = mapOpts.controls.geocoder || {}
		mapOpts.controls.geocoder.show = mapOpts.controls.geocoder.show || false;
		mapOpts.controls.geocoder.position = mapOpts.controls.geocoder.position || 'top-right';
		mapOpts.controls.scale = mapOpts.controls.scale || {}
		mapOpts.controls.scale.show = mapOpts.controls.scale.show || false;
		mapOpts.controls.scale.position = mapOpts.controls.scale.position || 'bottom-left';
		mapOpts.controls.scale.unit = mapOpts.controls.scale.unit || 'metric';
		mapOpts.controls.scale.maxWidth = mapOpts.controls.scale.maxWidth || 110;
		mapOpts.controls.type = mapOpts.controls.type || {}
		mapOpts.controls.type.show = mapOpts.controls.type.show || false;
		mapOpts.controls.type.position = mapOpts.controls.type.position || 'top-left';
		mapOpts.controls.type.mapLabel = mapOpts.controls.type.mapLabel || 'Map';
		mapOpts.controls.type.satLabel = mapOpts.controls.type.satLabel || 'Satellite';
		mapOpts.controls.fullscreen = mapOpts.controls.fullscreen || {}

		if(typeof mapOpts.controls.fullscreen.show === 'undefined')
			mapOpts.controls.fullscreen.show = true;

		var controls = mapOpts.controls;

		// @todo handle locations

		/*		if(controls.zoom.show) {
					var offset = mapOpts.driver == 'google' ? 90 : 115;
					switch(controls.zoom.position) {
						case 'top-left': this.cornerSize.topLeft.h += offset; break;
						case 'top-right': this.cornerSize.topRight.h += offset; break;
						case 'bottom-left': this.cornerSize.bottomLeft.h += offset; break;
						case 'bottom-right': this.cornerSize.bottomRight.h += offset; break;
					}
				}
				if(controls.type.show) {
					var offset = mapOpts.driver == 'google' ? 70 : 40;
					switch(controls.type.position) {
						case 'top-left': this.cornerSize.topLeft.h += offset; break;
						case 'top-right': this.cornerSize.topRight.h += offset; break;
						case 'bottom-left': this.cornerSize.bottomLeft.h += offset; break;
						case 'bottom-right': this.cornerSize.bottomRight.h += offset; break;
					}
				}*/

		var self = this,
			createFuncs = {
				google: function(mapOpts) {
					self.cornerSize.bottomLeft.h += 25;
					self.cornerSize.bottomRight.h += 10;

					var positions = {
						'top-left': google.maps.ControlPosition.LEFT_TOP,
						'top-right': google.maps.ControlPosition.RIGHT_TOP,
						'bottom-left': google.maps.ControlPosition.LEFT_BOTTOM,
						'bottom-right': google.maps.ControlPosition.RIGHT_BOTTOM
					};
					self.driver = 'google';

					self._map = new google.maps.Map(
						document.getElementById(mapOpts.container),
						{
							zoom: mapOpts.zoom,
							center: new google.maps.LatLng(mapOpts.center[0], mapOpts.center[1]),
							scrollwheel: mapOpts.scrollZoom || false,
							zoomControl: controls.zoom.show,
							zoomControlOptions: {
								position: positions[controls.zoom.position]
							},
							streetViewControl: false,
							streetViewControlOptions: {
								position: positions['bottom-right']
							},
							scaleControl: controls.scale.show,
							gestureHandling: 'cooperative',
							mapTypeControl: controls.type.show,
							mapTypeControlOptions: {
								position: positions[controls.type.position]
							},
							fullscreenControl: controls.fullscreen.show,
							fullscreenControlOptions: {
								position: positions['bottom-left']
							}
						}
					);

					google.maps.event.addListenerOnce(self._map, 'idle', function () {
						if(mapOpts.onDragend) {
							google.maps.event.addListener(self._map, 'dragend', function() {
								mapOpts.onDragend(self);
							});
						}

						if(mapOpts.onZoom) {
							google.maps.event.addListener(self._map, 'zoom_changed', function() {
								mapOpts.onZoom(self);
							});
						}

						if(mapOpts.onClicked) {
							google.maps.event.addListener(self._map, "click", function(event) {
								mapOpts.onClicked(self);
							});
						}

						if(mapOpts.onLoaded)
							mapOpts.onLoaded(self);
					});

					// If using the geocoder
					if(controls.geocoder.show && mapOpts.onGeocodeResult) {
						var el = document.createElement('div'),
							ei = document.createElement('input');

						el.id = 'floating-panel-' + flMaps.mapCount;
						el.style.position = 'absolute';
						el.style.zIndex = '5';
						el.style.backgroundColor = '#fff';
						el.style.color = '#555';
						el.style.padding = '5px';
						el.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.24)';

						switch(controls.geocoder.position) {
							case 'top-left':
								el.style.top = (self.cornerSize.topLeft.h + 10) + 'px';
								el.style.left = '10px';
								break;
							case 'top-right':
								el.style.top = (self.cornerSize.topRight.h + 10) + 'px';
								el.style.right = '10px';
								break;
							case 'bottom-left':
								el.style.bottom = (self.cornerSize.bottomLeft.h + 10) + 'px';
								el.style.left = '10px';
								break;
							case 'bottom-right':
								el.style.bottom = (self.cornerSize.bottomRight.h + 10) + 'px';
								el.style.right = '10px';
								break;
						}

						ei.id = 'googleMapAddress-' + flMaps.mapCount;
						ei.type = 'search';
						ei.placeholder = 'Search';
						ei.style.padding = '5px';
						ei.style.fontSize = '1rem';
						ei.style.border = 'none';
						ei.style.outline = 'none';
						el.appendChild(ei);
						self._map.getDiv().appendChild(el);

						var geocoder = new google.maps.Geocoder(),
							addrElem = document.getElementById('googleMapAddress-' + flMaps.mapCount);
						addrElem.addEventListener('keydown', function(evt) {
							if(evt.key === 'Enter') {
								evt.preventDefault();

								var address = addrElem.value;
								geocoder.geocode({'address': address}, function(results, status) {
									if (status === 'OK') {
										if(mapOpts.onGeocodeResult) {
											mapOpts.onGeocodeResult(
												self,
												results[0].geometry.location.lat(),
												results[0].geometry.location.lng(),
												results[0]
											);
										}
									} else {
										alert('Geocode was not successful for the following reason: ' + status);
									}
								});
							}
						});
					}

					flMaps.mapCount++;
				},
				mapbox: function(mapOpts) {
					self.driver = 'mapbox';

					self._map = new mapboxgl.Map({
						container: mapOpts.container,
						style: mapOpts.style,
						center: [mapOpts.center[1], mapOpts.center[0]],
						zoom: mapOpts.zoom - 1,
						scrollZoom: mapOpts.scrollZoom || false,
						pitchWithRotate: false
					});
					self._map.touchZoomRotate.disableRotation();

					// Enable 2 finger panning
					var mapDiv = self._map.getContainer();
					mapDiv.addEventListener(
						'touchstart',
						function(e) {
							self._map.dragPan.disable();

							self.panStart.x = self.panStart.y = 0;
							for(var touch=0;touch < e.touches.length; touch++) {
								self.panStart.x += e.touches[touch].screenX;
								self.panStart.y += e.touches[touch].screenY;
							}

							self.panStart.x /= e.touches.length;
							self.panStart.y /= e.touches.length;
						}
					);
					mapDiv.addEventListener(
						'touchmove',
						function(e) {
							var x = 0,
								y = 0;

							for(var touch = 0; touch < e.touches.length; touch++) {
								x += e.touches[touch].screenX;
								y += e.touches[touch].screenY;
							}

							var movex = (x = (x / e.touches.length)) - self.panStart.x,
								movey = (y = (y / e.touches.length)) - self.panStart.y;

							self.panStart.x = x;
							self.panStart.y = y;

							if(e.touches.length === 2) {
								self._map.panBy(
									[-movex, -movey],
									{animate: false}
								)
							}
							else if(e.touches.length === 1 && self._map.getCanvas() == e.target) {
								window.scrollTo(window.pageXOffset, window.pageYOffset - movey);
							}
						}
					);
					mapDiv.addEventListener(
						'touchend',
						function(e) {
							self._map.dragPan.enable();
						}
					);

					if(controls.scale.show) {
						self._map.addControl(
							new mapboxgl.ScaleControl({
								maxWidth: controls.scale.maxWidth,
								unit: controls.scale.unit
							}),
							controls.scale.position
						);
					}
					if(controls.zoom.show) {
						self._map.addControl(
							new mapboxgl.NavigationControl({
								showCompass: false
							}),
							controls.zoom.position
						);
					}

					self._map.on('load', function() {
						if(mapOpts.onDragend) {
							self._map.on('dragend', function() {
								mapOpts.onDragend(self);
							});
						}

						if(mapOpts.onZoom) {
							self._map.on('zoomend', function() {
								mapOpts.onZoom(self);
							});
						}

						if(mapOpts.onClicked) {
							self._map.on('click', function() {
								mapOpts.onClicked(self);
							});
						}

						if(mapOpts.onLoaded)
							mapOpts.onLoaded(self);
					});

					// If using the geocoder
					if(controls.geocoder.show && mapOpts.onGeocodeResult) {
						// If geocoder not loaded
						if(!flMaps.apiState.mapbox.geocoderLoaded) {

							// Queue the callback
							flMaps.apiState.mapbox.geocoderReady.push({
								controls: controls,
								mapOpts: mapOpts,
								map: self
							});

							if(!flMaps.apiState.mapbox.geocoderLoading) {
								flMaps.apiState.mapbox.geocoderLoading = true;

								var link = document.createElement('link');
								link.rel = 'stylesheet';
								link.type = 'text/css';
								link.href = 'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-geocoder/v4.4.1/mapbox-gl-geocoder.css';
								link.media = 'all';
								document.getElementsByTagName('head')[0].appendChild(link);

								script = document.createElement('script');
								script.type = 'text/javascript';
								script.src = 'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-geocoder/v4.4.1/mapbox-gl-geocoder.min.js';
								script.onload = function() {
									flMaps.apiState.mapbox.geocoderLoaded = true;

									for(var i in flMaps.apiState.mapbox.geocoderReady) {
										// Configure geocoder
										var geocoder = new MapboxGeocoder({
											accessToken: mapboxgl.accessToken,
											flyTo: false,
											mapboxgl: mapboxgl,
											marker: false
										});
										flMaps.apiState.mapbox.geocoderReady[i].map._map.addControl(
											geocoder,
											(flMaps.apiState.mapbox.geocoderReady[i].controls.geocoder || {}).position || 'top-right'
										);
										geocoder.on('result', function(ev) {
											mapOpts.onGeocodeResult(
												self,
												ev.result.geometry.coordinates[1].toFixed(8),
												ev.result.geometry.coordinates[0].toFixed(8),
												ev.result
											);
										});
									}
								};
								document.getElementsByTagName('head')[0].appendChild(script);
							}
						}
						else {
							// Configure geocoder
							var geocoder = new MapboxGeocoder({
								accessToken: mapboxgl.accessToken,
								flyTo: false,
								mapboxgl: mapboxgl
							});
							self._map.addControl(
								geocoder,
								(controls.geocoder || {}).position || 'top-right'
							);
							geocoder.on('result', function(ev) {
								mapOpts.onGeocodeResult(
									self,
									ev.result.geometry.coordinates[1].toFixed(8),
									ev.result.geometry.coordinates[0].toFixed(8),
									ev.result
								);
							});
						}
					}

					if(controls.type.show && mapOpts.satellite) {
						// Create the sat / map view toggle
						var el = document.createElement('div');
						el.className = 'mapboxglShowMapSat';

						switch(controls.type.position) {
							case 'top-left':
								el.style.top = (self.cornerSize.topLeft.h + 10) + 'px';
								el.style.left = '10px';
								break;
							case 'top-right':
								el.style.top = (self.cornerSize.topRight.h + 10) + 'px';
								el.style.right = '10px';
								break;
							case 'bottom-left':
								el.style.bottom = (self.cornerSize.bottomLeft.h + 10) + 'px';
								el.style.left = '10px';
								break;
							case 'bottom-right':
								el.style.bottom = (self.cornerSize.bottomRight.h + 10) + 'px';
								el.style.right = '10px';
								break;
						}
						var container = $(el);
						container.html('<span class="mapboxglShowMap">' + controls.type.mapLabel + '</span><span class="mapboxglShowSat">' + controls.type.satLabel + '</span>');
						self._map.getContainer().appendChild(el);
						container.find('.mapboxglShowMap').on('click', function() {
							if(container.hasClass('mapboxglShowingSat')) {
								container.removeClass('mapboxglShowingSat');
								self._map.setStyle(mapOpts.style);
							}
						});
						container.find('.mapboxglShowSat').on('click', function() {
							if(!container.hasClass('mapboxglShowingSat')) {
								container.addClass('mapboxglShowingSat');
								self._map.setStyle(mapOpts.satellite);
							}
						});
					}

					// Test if can do fullscreen
					if(controls.fullscreen.show) {
						var el = document.createElement('div'),
							canDoFullScreen = (el.requestFullscreen || false) ||
								(el.mozRequestFullScreen || false) ||
								(el.webkitRequestFullscreen || false) ||
								(el.msRequestFullscreen || false);

						if (canDoFullScreen && controls.fullscreen.show) {
							// Create fullscreen toggle
							el.className = 'mapboxglGoFullscreen';
							el.style.bottom = (self.cornerSize.bottomLeft.h + 90) + 'px';
							el.style.left = '10px';

							/*						switch(controls.type.position) {
														case 'top-left':
															el.style.top = (self.cornerSize.topLeft.h + 10) + 'px';
															el.style.left = '10px';
															break;
														case 'top-right':
															el.style.top = (self.cornerSize.topRight.h + 10) + 'px';
															el.style.right = '10px';
															break;
														case 'bottom-left':
															el.style.bottom = (self.cornerSize.bottomLeft.h + 10) + 'px';
															el.style.left = '10px';
															break;
														case 'bottom-right':
															el.style.bottom = (self.cornerSize.bottomRight.h + 10) + 'px';
															el.style.right = '10px';
															break;
													}*/
							var container = $(el);
							container.html('<div></div><div></div><div></div><div></div>');
							self._map.getContainer().appendChild(el);

							container.on('click', function () {
								// If not full screen
								if (!container.hasClass('mapboxglInFullscreen')) {
									container.addClass('mapboxglInFullscreen');

									var elem = self._map.getContainer();
									if (elem.requestFullscreen)
										elem.requestFullscreen();
									else if (elem.mozRequestFullScreen) /* Firefox */
										elem.mozRequestFullScreen();
									else if (elem.webkitRequestFullscreen) /* Chrome, Safari and Opera */
										elem.webkitRequestFullscreen();
									else if (elem.msRequestFullscreen) /* IE/Edge */
										elem.msRequestFullscreen();
								} else {
									container.removeClass('mapboxglInFullscreen');

									var elem = self._map.getContainer();
									if (document.exitFullscreen)
										document.exitFullscreen();
									else if (document.mozCancelFullScreen) /* Firefox */
										document.mozCancelFullScreen();
									else if (document.webkitExitFullscreen) /* Chrome, Safari and Opera */
										document.webkitExitFullscreen();
									else if (document.msExitFullscreen) /* IE/Edge */
										document.msExitFullscreen();
								}

								self._map.resize();
							});

							function exitFullscreen(evt) {
								var isFullScreen = (document.webkitIsFullScreen || false) ||
									(document.mozFullScreen || false) ||
									(document.msFullscreenElement || false);
								if (!isFullScreen) {
									container.removeClass('mapboxglInFullscreen');
								}
								self._map.resize();
							}

							$(document).on('fullscreenchange', exitFullscreen);
							$(document).on('mozfullscreenchange', exitFullscreen);
							$(document).on('webkitfullscreenchange', exitFullscreen);
							$(document).on('MSFullscreenChange', exitFullscreen);
						}
					}

					flMaps.mapCount++;
				}
			};

		// Test if API loaded
		if(!flMaps.apiState[mapOpts.driver].loaded) {

			/**
			 * Process the pending queue.
			 */
			function processQueue() {
				setTimeout(function() {
					for(var i in flMaps.apiState[mapOpts.driver].readyFunctions) {
						createFuncs[mapOpts.driver](flMaps.apiState[mapOpts.driver].readyFunctions[i].mapOpts);
						flMaps.apiState[mapOpts.driver].readyFunctions[i].callback(self);
					}
				}, 10);
			}

			// Queue the callback
			flMaps.apiState[mapOpts.driver].readyFunctions.push({
				mapOpts: mapOpts,
				callback: callback
			});

			// If not currently loading then trigger load
			if(!flMaps.apiState[mapOpts.driver].loading) {
				flMaps.apiState[mapOpts.driver].loading = true;
				var script;

				// Load Google Maps
				if(mapOpts.driver === 'google') {
					script = document.createElement('script');
					script.type = 'text/javascript';
					script.src = 'https://maps.googleapis.com/maps/api/js?key=' + flMaps.apiState[mapOpts.driver].key;
					script.onload = function() {
						flMaps.apiState[mapOpts.driver].loaded = true;
						processQueue();
					};
					document.getElementsByTagName('head')[0].appendChild(script);
				}
				// Load Mapbox
				else if(mapOpts.driver === 'mapbox') {
					var link = document.createElement('link');
					link.rel = 'stylesheet';
					link.type = 'text/css';
					link.href = 'https://api.mapbox.com/mapbox-gl-js/v2.9.1/mapbox-gl.css';
					link.media = 'all';
					document.getElementsByTagName('head')[0].appendChild(link);

					script = document.createElement('script');
					script.type = 'text/javascript';
					script.src = 'https://api.mapbox.com/mapbox-gl-js/v2.9.1/mapbox-gl.js';
					script.onload = function() {
						flMaps.apiState[mapOpts.driver].loaded = true;
						mapboxgl.accessToken = flMaps.apiState[mapOpts.driver].key;
						processQueue();
					};
					document.getElementsByTagName('head')[0].appendChild(script);

					// Custom CSS for mapbox
					var css = '.mapboxglShowMapSat { border-radius: 2px; position: absolute; z-index: 5; background-color: #fff; color: #555; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.24); }' +
						'.mapboxglShowMapSat span { font-weight: normal; display: inline-block; cursor: pointer; padding: 10px 15px; }' +
						'.mapboxglShowMapSat span:hover { background-color: #e0e0e0; }' +
						'.mapboxglShowMapSat span:first-child { font-weight: bold; border-right: 1px solid #ddd; }' +
						'.mapboxglShowMapSat.mapboxglShowingSat span:first-child { font-weight: normal; }' +
						'.mapboxglShowMapSat.mapboxglShowingSat span:last-child { font-weight: bold; }' +
						'.mapboxgl-control-container .mapboxgl-ctrl-geocoder--input { padding: .5rem .5rem .5rem 2rem !important; }' +
						'.mapboxgl-control-container button { margin: 0; box-shadow: none; }' +
						'.mapboxglGoFullscreen { cursor: pointer; position: absolute; z-index: 5; background-color: #fff; color: #555; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.24); width: 40px; height: 40px; }' +
						'.mapboxglGoFullscreen div { position: absolute; width: 5px; height: 5px; }' +
						'.mapboxglGoFullscreen div:nth-child(1) { left: 10px; top: 10px; border-top: 2px solid #555; border-left: 2px solid #555; }' +
						'.mapboxglGoFullscreen div:nth-child(2) { right: 10px; top: 10px; border-top: 2px solid #555; border-right: 2px solid #555; }' +
						'.mapboxglGoFullscreen div:nth-child(3) { right: 10px; bottom: 10px; border-bottom: 2px solid #555; border-right: 2px solid #555; }' +
						'.mapboxglGoFullscreen div:nth-child(4) { left: 10px; bottom: 10px; border-bottom: 2px solid #555; border-left: 2px solid #555; }' +
						'.mapboxglGoFullscreen.mapboxglInFullscreen div:nth-child(1) { left: initial; top: initial; right: 10px; bottom: 10px; }' +
						'.mapboxglGoFullscreen.mapboxglInFullscreen div:nth-child(2) { right: initial; top: initial; left: 10px; bottom: 10px; }' +
						'.mapboxglGoFullscreen.mapboxglInFullscreen div:nth-child(3) { right: initial; bottom: initial; left: 10px; top: 10px; }' +
						'.mapboxglGoFullscreen.mapboxglInFullscreen div:nth-child(4) { left: initial; bottom: initial; right: 10px; top: 10px; }';
					var style = document.createElement('style');
					if(style.styleSheet)
						style.styleSheet.cssText = css;
					else
						style.appendChild(document.createTextNode(css));
					document.getElementsByTagName('head')[0].appendChild(style);
				}
				else {
					throw 'Unknown mapping API ' + mapOpts.driver;
				}
			}
		}
		else {
			createFuncs[mapOpts.driver](mapOpts);
			callback(self);
		}

		/**
		 * Get the current zoom level.
		 * @returns {number}
		 */
		this.getZoom = function() {
			return this._map.getZoom() + (this.driver === 'mapbox' ? 1 : 0);
		};

		/**
		 * Set the map zoom level.
		 * @param {number} zoom The zoom level.
		 * @returns {flMaps.Map}
		 */
		this.setZoom = function(zoom) {
			this._map.setZoom(zoom - (this.driver === 'mapbox' ? 1 : 0));
			return this;
		};

		/**
		 * Get the map center.
		 *
		 * @returns {{lat:number, lng:number}}
		 */
		this.getCenter = function() {
			var c = this._map.getCenter();
			if(this.driver === 'google')
				return { lat: c.lat(), lng: c.lng() };
			else
				return c;
		};

		/**
		 * Set the map center.
		 * @param {number} lat Latitude
		 * @param {number} lng Longitude
		 * @returns {flMaps.Map}
		 */
		this.setCenter = function(lat, lng) {
			if(this.driver === 'google')
				this._map.setCenter({lat: lat, lng: lng});
			else
				this._map.setCenter([lng, lat]);
			return this;
		};

		/**
		 * Pan the map to a location.
		 * @param {number} lat Latitude
		 * @param {number} lng Longitude
		 * @returns {flMaps.Map}
		 */
		this.panTo = function(lat, lng) {
			if(this.driver === 'google')
				this._map.panTo({lat: lat, lng: lng});
			else
				this._map.panTo([lng, lat]);
			return this;
		};

		/**
		 * Fit the map to the given bounds.
		 * @param {flMaps.LatLngBounds} bounds
		 * @param {number|{top:number, right:number, bottom:number, left:number}} padding Optional padding.
		 * @returns {flMaps}
		 */
		this.fitBounds = function(bounds, padding) {
			padding = padding || 0;

			if(this.driver === 'google') {
				this._map.fitBounds(
					{
						north: bounds.getNorth(),
						east: bounds.getEast(),
						south: bounds.getSouth(),
						west: bounds.getWest()
					},
					padding
				);
			}
			else {
				this._map.fitBounds(
					new mapboxgl.LngLatBounds(
						new mapboxgl.LngLat(bounds.getWest(), bounds.getSouth()),
						new mapboxgl.LngLat(bounds.getEast(), bounds.getNorth())
					),
					{ padding: padding }
				);
			}
			return this;
		};

		/**
		 * Resize the map to its container.
		 * @returns {flMaps}
		 */
		this.resize = function() {
			if(this.driver === 'google') {
				google.maps.event.trigger(this._map, 'resize');
			}
			else {
				this._map.resize();
			}
			return this;
		};
	},

	/**
	 * Lat / Lng object.
	 * @param {number} lat Latitude
	 * @param {number} lng Longitude
	 * @constructor
	 * @property lat: number
	 * @property lng: number
	 */
	LatLng: function(lat, lng) {
		this.lat = lat;
		this.lng = lng;
	},

	/**
	 * Lat / Lng bounds object.
	 * @param {flMaps.LatLng | flMaps.LatLng[]} corner1
	 * @param {flMaps.LatLng} corner2
	 * @constructor
	 * @property {flMaps.LatLng} southWest
	 * @property {flMaps.LatLng} northEast
	 */
	LatLngBounds: function(corner1, corner2) {
		this.southWest = this.northEast = null;

		/**
		 * Extend the bounds.
		 * @param {flMaps.LatLng|number[]} latlng
		 * @returns {flMaps.LatLngBounds}
		 */
		this.extend = function(latlng) {
			latlng = flMaps._toLatLng(latlng);

			if (!this.southWest && !this.northEast) {
				this.southWest = new flMaps.LatLng(latlng.lat, latlng.lng);
				this.northEast = new flMaps.LatLng(latlng.lat, latlng.lng);
			} else {
				this.southWest.lat = Math.min(latlng.lat, this.southWest.lat);
				this.southWest.lng = Math.min(latlng.lng, this.southWest.lng);
				this.northEast.lat = Math.max(latlng.lat, this.northEast.lat);
				this.northEast.lng = Math.max(latlng.lng, this.northEast.lng);
			}

			return this;
		};

		/**
		 * Get the north latitude.
		 * @returns {number}
		 */
		this.getNorth = function() {
			return this.northEast.lat;
		};

		/**
		 * Get the east longitude.
		 * @returns {number}
		 */
		this.getEast = function() {
			return this.northEast.lng;
		};

		/**
		 * Get the south latitude.
		 * @returns {number}
		 */
		this.getSouth = function() {
			return this.southWest.lat;
		};

		/**
		 * Get the west longitude.
		 * @returns {number}
		 */
		this.getWest = function() {
			return this.southWest.lng;
		};

		// Initialise the object
		var latlngs = corner2 ? [corner1, corner2] : corner1;
		for(var i = 0, len = latlngs.length; i < len; i++) {
			this.extend(latlngs[i]);
		}
	},

	/**
	 * Convert the input to a lat lng object.
	 *
	 * @param {number|number[]} a
	 * @param {number} b
	 * @returns {flMaps.LatLng}
	 * @private
	 */
	_toLatLng: function(a, b) {
		if(a instanceof flMaps.LatLng)
			return a;

		if(a instanceof Array) {
			if(a.length == 2)
				return new flMaps.LatLng(a[0], a[1]);
			return null;
		}

		if(b === undefined)
			return null;

		return new flMaps.LatLng(a, b);
	}
};
